import React, { useCallback, useContext, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { ScreenSize, UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { ISelectableRealestate } from "src/stores/RealestateStore";
import {
    belongsToConsolidation,
    getDisplayedRealestates,
    getRealestatesToAdd
} from "src/components/realestates/add-realestates-modal/AddRealestatesModal.utils";
import styles from "./AddRealestatesModalList.module.css";

interface IAddRealestatesModalListProps {
    realestates: ISelectableRealestate[];
    selectedRealestateIds: string[];
    emptyListMessage: string;
    onAdd: (realestates: ISelectableRealestate[]) => void;
}

const COLUMN_WIDTHS = [1, 1];
const REALESTATES_PER_PAGE = 10;

const AddRealestatesModalListBase = (props: IAddRealestatesModalListProps) => {
    const { t } = useTranslation();
    const { uiStore } = useContext(RootStoreContext);
    const { realestates, selectedRealestateIds, emptyListMessage, onAdd } = props;

    const [searchQuery, setSearchQuery] = useState<string>("");

    const displayedRealestates = useMemo(
        () => getDisplayedRealestates(realestates, selectedRealestateIds, searchQuery),
        [realestates, selectedRealestateIds, searchQuery]
    );

    const onSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    }, []);

    const onClickRealestate = useCallback(
        (realestate: ISelectableRealestate) => (e: React.MouseEvent) => {
            // UI.List.Item wraps a clickable row in a router link without target, it must not navigate.
            e.preventDefault();
            onAdd(getRealestatesToAdd(realestate, realestates, selectedRealestateIds));
        },
        [onAdd, realestates, selectedRealestateIds]
    );

    const renderItem = useCallback(
        (realestate: ISelectableRealestate, screenSize: ScreenSize) => {
            const nameClassName = belongsToConsolidation(realestate) ? styles.RealestateOfConsolidation : undefined;

            const listRows =
                screenSize === ScreenSize.MOBILE
                    ? [
                          <UI.List.Row key={`row-mobile-${realestate.id}`}>
                              <div className={nameClassName}>
                                  <strong>{realestate.name}</strong>
                                  <div className={styles.MobileNumber}>{realestate.number}</div>
                              </div>
                          </UI.List.Row>
                      ]
                    : [
                          <UI.List.Row key={`row-${realestate.id}`}>
                              <UI.List.Cell
                                  key="name"
                                  colspan={COLUMN_WIDTHS[0]}
                                  value={<div className={nameClassName}>{realestate.name}</div>}
                              />
                              <UI.List.Cell key="number" colspan={COLUMN_WIDTHS[1]} value={realestate.number} />
                          </UI.List.Row>
                      ];

            return (
                <UI.List.Item
                    key={`item-${realestate.id}`}
                    screenSize={screenSize}
                    rows={listRows}
                    className={styles.ListItem}
                    onClick={onClickRealestate(realestate)}
                    isClickableItemWithoutChevron
                />
            );
        },
        [onClickRealestate]
    );

    const renderHeader = useCallback(
        (screenSize: ScreenSize) => {
            if (screenSize === ScreenSize.MOBILE) {
                return <></>;
            }

            const headerCells = [
                <UI.List.Cell key="name" colspan={COLUMN_WIDTHS[0]} value={t("screens.realestate.title")} truncate />,
                <UI.List.Cell
                    key="number"
                    colspan={COLUMN_WIDTHS[1]}
                    value={t("screens.realestate.dashboard.number")}
                    truncate
                />
            ];

            return <UI.List.Header rows={headerCells} className="p-0" />;
        },
        [t]
    );

    return (
        <>
            <div className="mb-4">
                <UI.Input
                    type="search"
                    onChange={onSearch}
                    placeholder={t("labels.search").toString()}
                    value={searchQuery}
                />
            </div>
            <UI.List.BasicList
                screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                items={displayedRealestates}
                renderItem={renderItem}
                renderHeader={renderHeader}
                itemsPerPage={REALESTATES_PER_PAGE}
                emptyListMessage={emptyListMessage}
            />
        </>
    );
};

export const AddRealestatesModalList = Sentry.withProfiler(observer(AddRealestatesModalListBase));
